import type { UnifiedInvoice } from '../../../../lib/userInvoicesApi';

interface UnifiedInvoicesTableProps {
  invoices: UnifiedInvoice[];
  onRowClick: (invoice: UnifiedInvoice) => void;
}

const SOURCE_STYLES: Record<string, string> = {
  smartbill: 'bg-purple-100 text-purple-700',
  manual: 'bg-blue-100 text-blue-700',
  email: 'bg-amber-100 text-amber-700',
};

const SOURCE_LABELS: Record<string, string> = {
  smartbill: 'SmartBill',
  manual: 'Manual',
  email: 'Email',
};

// Status colours are best-effort across both source vocabularies (SmartBill
// payment status + Invoice status). Unknown statuses fall back to neutral grey.
const STATUS_STYLES: Record<string, string> = {
  PAID: 'bg-green-100 text-green-700',
  UNPAID: 'bg-red-100 text-red-700',
  PARTIAL: 'bg-amber-100 text-amber-700',
  UNKNOWN: 'bg-gray-100 text-gray-500',
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  OVERDUE: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-400 line-through',
};

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ro-RO') : '—';
}

function formatMoney(amount: number, currency: string): string {
  return `${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

export function UnifiedInvoicesTable({ invoices, onRowClick }: UnifiedInvoicesTableProps) {
  if (invoices.length === 0) {
    return <p className="py-8 text-center text-gray-500">Nicio factură găsită.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Număr</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Dată</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Descriere</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Categorie</th>
            <th className="px-3 py-2 text-right font-medium text-gray-600">Net</th>
            <th className="px-3 py-2 text-right font-medium text-gray-600">TVA</th>
            <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">Sursă</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {invoices.map((inv) => (
            <tr
              key={`${inv.source}-${inv.id}`}
              onClick={() => onRowClick(inv)}
              className="cursor-pointer hover:bg-gray-50"
            >
              <td className="px-3 py-2 font-medium text-gray-900">
                {inv.invoiceNumber}
                <span
                  className={`ml-2 align-middle text-xs ${
                    inv.flow === 'income' ? 'text-green-600' : 'text-gray-500'
                  }`}
                >
                  {inv.flow === 'income' ? '↑' : '↓'}
                </span>
              </td>
              <td className="px-3 py-2 text-gray-700">{formatDate(inv.issueDate)}</td>
              <td className="px-3 py-2 text-gray-700">{inv.description ?? '—'}</td>
              <td className="px-3 py-2 text-gray-700">{inv.category ?? '—'}</td>
              <td className="px-3 py-2 text-right text-gray-700">
                {formatMoney(inv.net, inv.currency)}
              </td>
              <td className="px-3 py-2 text-right text-gray-700">
                {formatMoney(inv.vat, inv.currency)}
              </td>
              <td className="px-3 py-2 text-right font-medium text-gray-900">
                {formatMoney(inv.total, inv.currency)}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    STATUS_STYLES[inv.status] ?? 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {inv.status}
                </span>
              </td>
              <td className="px-3 py-2">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    SOURCE_STYLES[inv.source] ?? 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {SOURCE_LABELS[inv.source] ?? inv.source}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
